import { useCallback, useContext, useEffect } from 'react';

import { V3AppsContext } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider';
import EntityDefinitionsContext from 'contexts/EntityDefinitionsContext';
import VisualisationCacheContext from 'contexts/VisualisationCacheContext';
import getDashboardIdsInApp from 'hooks/useHasAccess/getDashboardIdsInApp';
import usePopupReports, {
  isDashboardPopupReportUsage,
  isKpiListPopupReportUsage,
} from 'contextProviders/SplashScreenProviders/ContentDependantProviders/PopupReportUsageLookupProvider/usePopupReports';
import getReportIdsInApp from '../../hooks/useHasAccess/getReportIdsInApp';
import getScorecardIdsInApp from '../../hooks/useHasAccess/getScorecardIdsInApp';
import useGetContentAppLink from './useGetContentAppLink';

export type ContentUsageType = 'dashboard' | 'report' | 'scorecard';
type DashboardUsageResult = {
  v3Apps: V3.App[];
  entityApps: EntityDetails.App[];
  popupReports: ReportType[];
};

const useGetContentUsage = () => {
  const { allProductApps, allWorkspaces } = useContext(V3AppsContext);
  const { entityDetailsApps } = useContext(EntityDefinitionsContext);
  const { requestAllDashboardGadgets } = useContext(VisualisationCacheContext);
  const getContentAppLink = useGetContentAppLink();
  const popupReportsData = usePopupReports({ onlyFromPublishedSources: false });

  // Trigger loading all gadgets so popup report lookup can be computed
  useEffect(() => {
    requestAllDashboardGadgets();
  }, [requestAllDashboardGadgets]);

  const getV3AppUsages = useCallback(
    (typeId: string, type: ContentUsageType): V3.App[] => {
      const apps: V3.App[] = [...allProductApps, ...allWorkspaces];
      const usageApps: V3.App[] = [];

      let ids: string[];
      apps.forEach((app) => {
        if (type === 'dashboard') {
          ids = getDashboardIdsInApp({ app });
        } else if (type === 'report') {
          ids = getReportIdsInApp({ app });
        } else if (type === 'scorecard') {
          ids = getScorecardIdsInApp({ app });
        } else {
          const e = new Error();
          e.name = `getV3AppUsages: invalid type (${type})`;
          throw e;
        }

        if (ids.includes(typeId)) {
          usageApps.push(app);
          getContentAppLink({ app, itemId: typeId });
        }
      });

      return usageApps;
    },
    [allProductApps, allWorkspaces, getContentAppLink],
  );

  const getEntityAppUsages = useCallback(
    (typeId: string, type: ContentUsageType): EntityDetails.App[] => {
      return entityDetailsApps.filter((app) =>
        app.tabs.some((t) => t.type === type && t.typeId === typeId),
      );
    },
    [entityDetailsApps],
  );

  const getPopupReportUsages = useCallback(
    (typeId: string, type: ContentUsageType) => {
      const usages: ReportType[] = [];
      const usageIds: string[] = [];
      popupReportsData.forEach(({ report, usage }) => {
        usage.forEach((u) => {
          if (
            (type === 'dashboard' &&
              isDashboardPopupReportUsage(u) &&
              u.dashboard.id === typeId) ||
            (type === 'scorecard' &&
              isKpiListPopupReportUsage(u) &&
              u.kpiList.id === typeId)
          ) {
            if (usageIds.includes(report.id)) return;

            usageIds.push(report.id);
            usages.push(report);
          }
        });
      });
      return usages;
    },
    [popupReportsData],
  );

  const getUsage = useCallback(
    (typeId: string, type: ContentUsageType): DashboardUsageResult => {
      return {
        v3Apps: getV3AppUsages(typeId, type),
        entityApps: getEntityAppUsages(typeId, type),
        popupReports: getPopupReportUsages(typeId, type),
      };
    },
    [getEntityAppUsages, getPopupReportUsages, getV3AppUsages],
  );

  return getUsage;
};

export default useGetContentUsage;
